import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import {fetchBonusQuestionGameWeeks} from '../api';
import Section from '../components/Section';
import Select from '../components/Select';
import {CHAMPIONSHIP_OPTIONS, VISIBILITY_OPTIONS} from '../constants';
import {questionBonusStyles} from '../styles';
import {
  BonusQuestionGameWeek,
  DiffusionValues,
  QuestionBonusChampionshipId,
  QuestionBonusVisibilityHours,
} from '../types';
import {buildGameWeekOptions, formatGameWeekRange} from '../utils';

interface DiffusionSectionProps {
  values: DiffusionValues;
  onChange: (values: DiffusionValues) => void;
  readOnly?: boolean;
  /** Championnat et journée figés : ils ne sont modifiables qu'à la création. */
  isScopeLocked?: boolean;
}

export const DiffusionSection = ({
  values,
  onChange,
  readOnly = false,
  isScopeLocked = false,
}: DiffusionSectionProps) => {
  const dispatch = useDispatch();
  const [gameWeeks, setGameWeeks] = useState<BonusQuestionGameWeek[]>([]);
  const [isLoadingGameWeeks, setIsLoadingGameWeeks] = useState(false);

  useEffect(() => {
    setIsLoadingGameWeeks(true);
    dispatch(
      fetchBonusQuestionGameWeeks(
        values.championshipId,
        fetchedGameWeeks => {
          setGameWeeks(fetchedGameWeeks);
          setIsLoadingGameWeeks(false);
        },
        () => {
          setGameWeeks([]);
          setIsLoadingGameWeeks(false);
        },
      ),
    );
  }, [dispatch, values.championshipId]);

  const gameWeekOptions = useMemo(() => {
    // Portée figée : la journée courante porte déjà une question, donc elle ne
    // figure pas dans les options éligibles. On la réinjecte seule pour l'afficher.
    if (isScopeLocked) {
      if (!values.gameWeekNumber) return [];
      const currentGameWeek = gameWeeks.find(
        gameWeek => gameWeek.gameWeekNumber === values.gameWeekNumber,
      );
      return [
        {
          value: values.gameWeekNumber,
          label: `Journée ${values.gameWeekNumber}`,
          hint: currentGameWeek
            ? formatGameWeekRange(currentGameWeek)
            : undefined,
        },
      ];
    }
    return buildGameWeekOptions(gameWeeks);
  }, [gameWeeks, isScopeLocked, values.gameWeekNumber]);

  const setChampionshipId = (championshipId: QuestionBonusChampionshipId) =>
    onChange({...values, championshipId, gameWeekNumber: undefined});

  const setGameWeekNumber = (gameWeekNumber: number) =>
    onChange({...values, gameWeekNumber});

  const setVisibilityHours = (visibilityHours: QuestionBonusVisibilityHours) =>
    onChange({...values, visibilityHoursBeforeGameWeekStart: visibilityHours});

  return (
    <Section title="01 - Diffusion">
      <div style={questionBonusStyles.sectionRow}>
        <Select
          label="Championnat"
          // Un seul championnat ouvert pour le moment.
          disabled
          options={CHAMPIONSHIP_OPTIONS}
          value={values.championshipId}
          onChange={setChampionshipId}
        />
        <Select
          label="Gameweek"
          required
          placeholder="Sélectionner une gameweek"
          disabled={readOnly || isScopeLocked}
          options={gameWeekOptions}
          value={values.gameWeekNumber}
          onChange={setGameWeekNumber}
          emptyMessage={
            isLoadingGameWeeks
              ? 'Chargement des gameweeks…'
              : 'Aucune gameweek disponible'
          }
        />
        <Select
          label="Visibilité sur le jeu"
          disabled={readOnly}
          options={VISIBILITY_OPTIONS}
          value={values.visibilityHoursBeforeGameWeekStart}
          onChange={setVisibilityHours}
        />
      </div>
    </Section>
  );
};

export default DiffusionSection;



// WEBPACK FOOTER //
// ./src/pages/Other/MPP/QuestionBonus/Form/DiffusionSection.tsx